import React, { useRef,useState, useEffect } from "react";
import { Search, X, Minus, Plus, AlertCircle, Trash2 } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage ,faChevronDown,faSearch,faPlusCircle,faX,faMapMarkerAlt,faPaperPlane} from "@fortawesome/free-solid-svg-icons";
import ImageGallery from "./ImageGallery";
import { decryptData } from "../../../utils/storageCrypto";
import useCommonResponseHandler from '../../CommenResponseAndErrorHandlers/CommenResponseHandler';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

// Custom hook to handle outside clicks
const useOutsideClick = (ref, callback) => {
  const handleClick = (e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      callback();
    }
  };

  React.useEffect(() => {
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);
};

const CustomDropdown = ({ options, selected, onSelect, placeholder = "Select", showSearch = true, showAddNew = false, addNewText = "Add New Address" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(null);
  const dropdownRef = React.useRef(null);
  const searchInputRef = useRef(null);

  const filteredOptions = options.filter(option => {
    if (!option || !option.label) return false;
    return option.label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleOptionClick = (option) => {
    onSelect(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedOptionIndex(prevIndex => {
          const newIndex = prevIndex === null ? 0 : Math.min(prevIndex + 1, filteredOptions.length - 1);
          scrollToOption(newIndex);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedOptionIndex(prevIndex => {
          const newIndex = prevIndex === null ? filteredOptions.length - 1 : Math.max(prevIndex - 1, 0);
          scrollToOption(newIndex);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedOptionIndex !== null) {
          handleOptionClick(filteredOptions[focusedOptionIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  const scrollToOption = (index) => {
    const optionElements = dropdownRef.current.querySelectorAll('div[data-index]');
    if (optionElements && optionElements[index]) {
      optionElements[index].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  };

  useOutsideClick(dropdownRef, () => setIsOpen(false));
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className="flex justify-between items-center p-2 border bg-white border-gray-300 rounded-md cursor-pointer hover:border-blue-500 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all"
        onClick={() => setIsOpen(!isOpen)}
        tabIndex={0}
        role="button"
        onKeyDown={handleKeyDown}
      >
        <span className={`text-sm truncate ${selected?.label ? 'text-black font-medium' : 'text-[#838195]'}`}>
          {selected?.label || placeholder}
        </span>
        <FontAwesomeIcon 
          icon={faChevronDown} 
          className={`w-3 h-3 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </div>
      {isOpen && (
        <div className="absolute w-full max-h-40 bg-white border border-gray-300 rounded-md shadow-xl z-20 flex flex-col overflow-hidden">
          {/* Search Section - Fixed height */}
          {showSearch && (
            <div className="p-2 border-b border-gray-100 bg-gray-50 shrink-0">
              <input
                ref={searchInputRef}
                type="text"
                className="w-full p-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                placeholder=" Search addresses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
          )}
          
          {/* Options List - Scrollable, reduced height */}
          <div className="overflow-y-auto max-h-40 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent flex-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  data-index={index}
                  className={`px-4 py-1 cursor-pointer text-sm border-b border-gray-50 last:border-b-0 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 focus:bg-blue-100 transition-all duration-200 ${
                    index === focusedOptionIndex ? 'bg-blue-100 ring-1 ring-blue-200' : ''
                  }`}
                  onClick={() => handleOptionClick(option)}
                  role="option"
                >
                  <div className="flex items-center space-x-3">
                    {option.icon && <FontAwesomeIcon icon={option.icon} className="text-gray-500 w-4 h-4 flex-shrink-0" />}
                    <span className="font-medium text-gray-800 truncate">{option.label}</span>
                    {option.addressType && (
                      <span className="ml-auto text-xs bg-gray-100 px-2 py-0.5 rounded-full text-gray-600">
                        {option.addressType}
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-6 text-center">
                <FontAwesomeIcon icon={faSearch} className="mx-auto mb-2 text-gray-400 w-6 h-6" />
                <p className="text-sm text-gray-500">No addresses found</p>
              </div>
            )}
          </div>
          
          {/* Add New Button - Fixed height, always visible */}
         {showAddNew && (
  <div className="border-t border-gray-200 shrink-0">
    <button
      className="w-full flex items-center justify-start pl-4 pr-3 py-2.5 text-sm font-semibold text-blue-600 hover:text-blue-700 hover:bg-blue-100 transition-all duration-200 group"
      onClick={() => handleOptionClick({ value: 'add-new', label: addNewText })}
    >
      <FontAwesomeIcon icon={faPlusCircle} className="mr-2 w-4 h-4 group-hover:scale-110 transition-transform flex-shrink-0" />
      <span>{addNewText}</span>
    </button>
  </div>
)}
        </div>
      )}
    </div>
  );
};

const ViewMRitems = ({
  isOpen,
  onClose,
  MRDetails,
  MRitem,
  setMRItemsPoupMessage,
  setPopupType,
  fetchMrItems,
  setShowPopup,
}) => {
    const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Received":
        return "bg-yellow-100 text-yellow-800"; // Warning
      case "Verified":
        return "bg-blue-100 text-blue-800"; // Info
      case "Rejected":
        return "bg-red-100 text-red-800"; // Action
      case "RFQ Created":
        return "bg-green-100 text-green-800"; // Success
      case "Cancelled":
        return "bg-orange-100 text-orange-800"; // Cancelled  
      default:
        return "bg-gray-100 text-gray-800"; // Default
    }
  };
  const [pricingModel, setPricingModel] = useState("project");
  const [searchQuery, setSearchQuery] = useState("");
  const [items, setItems] = useState([]);
  const [originalItems, setOriginalItems] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [topAlert, setTopAlert] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(MRDetails?.customer || null);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedImageReason, setSelectedImageReason] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [deliveryAddresses, setDeliveryAddresses] = useState([]);
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState(null);
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [formData, setFormData] = useState({ projectName: '', areaLocalitySector: '', nearbyLandmark: '', addressType: 'project' });
  const [activeTab, setActiveTab] = useState('project');  const FetchProjectsUrl = process.env.REACT_APP_FORE_APILINK; //measurit
  console.log('MRDetails:', MRDetails);
  console.log('MRitem:', MRitem);
  const fetchurl = process.env.REACT_APP_FORE_BRICKBUCKET;
  const imageurl = process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES;
  const [customerId, setCustomerId] = useState(MRDetails?.customer?._id || null);
  const {setPopupMessage} = useCommonResponseHandler();

  // Add these to your component state (if not already present)
  // Add these states with existing states
  const [address, setAddress] = useState('');
  const [selectedAddressDetails, setSelectedAddressDetails] = useState('');
  const [searchText, setSearchText] = useState('');
  const [coordinates, setCoordinates] = useState({ lat: 0, lng: 0 });
  const [isMapFullyLoaded, setIsMapFullyLoaded] = useState(false);
  const [showDetailsForm, setShowDetailsForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const popupTimerRef = useRef(null);

  const showPopupWithTimer = (message, type, autoClose = true, duration = 3000) => {
    if (popupTimerRef.current) {
      clearTimeout(popupTimerRef.current);
      popupTimerRef.current = null;
    }

    setMRItemsPoupMessage(message);
    setPopupType(type);
    setShowPopup(true);

    if (autoClose) {
      popupTimerRef.current = setTimeout(() => {
        setShowPopup(false);
        popupTimerRef.current = null;
      }, duration);
    }
  };

  useEffect(() => {
    return () => {
      if (popupTimerRef.current) {
        clearTimeout(popupTimerRef.current);
      }
    };
  }, []);
  useEffect(() => {
    if (coordinates.lat && coordinates.lng) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        renderMap();
      }, 100);
    }
  }, [coordinates]);
  
  
      const renderMap = () => {
    if (!window.google || !coordinates.lat || !coordinates.lng) return;
    const mapElement = document.getElementById('rfq-map');
    if (!mapElement) return;
    mapElement.innerHTML = '';
    const map = new window.google.maps.Map(mapElement, {
      center: coordinates,
      zoom: 15
    });
  };

useEffect(() => {
  const id = selectedCustomer?._id || customerId;

  if (id) {
    fetchDeliveryAddresses(id);
  }
}, [selectedCustomer?._id, customerId]);

  // Fetch all products when popup opens
  useEffect(() => {
    if (isOpen) fetchAllItems();
  }, [isOpen]);

  console.log(MRitem)

  // Initialize MR data
  useEffect(() => {
    if (MRitem) {
      const pricing = MRitem.price_eligibility === "Project Pricing" ? "project" : "general";
      setPricingModel(pricing);

      console.log('MRitem:', MRitem);

      const mappedItems = MRitem.items.map((item) => ({
        product_id: item.product_id?._id || item.product_id,
        name: item.product_name,
        quantity: item.quantity || 1,
        product_category_id: item.product_id?.product_category_id || item.product_category_id,
        UOM_id: item.UOM_id?._id || item.UOM_id,
        unit: item.unit || item.UOM_id?.UOM,
        image: item.product_id?.product_image
          ? `${imageurl}${item.product_id.product_image}`
          : null,
      }));
     console.log ('items mapped', mappedItems)
      setItems(mappedItems);
      setOriginalItems(JSON.parse(JSON.stringify(mappedItems)));
      setUnsavedChanges(false);
    }
  }, [MRitem, isOpen]);

  const fetchAllItems = async () => {
    try {
         const encryptedToken = sessionStorage.getItem('token');
             const token = decryptData(encryptedToken);
             const encryptedhubId = sessionStorage.getItem('hubId');
             const hubId = decryptData(encryptedhubId);
      if (!hubId) return console.error("hub ID not found.");

      const res = await fetch(
        `${fetchurl}product/get-all-products?hub_id=${hubId}`,
        {
          headers: { "Content-Type": "application/json", "x-token": token },
        }
      );
      const data = await res.json();
      if (data.success) setAllProducts(data.data);
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  // Show top alert inside popup
  const showTopAlert = (message, type = "info") => {
    setTopAlert({ message, type });
    setTimeout(() => setTopAlert(null), 2500);
  };

  // Quantity change
 const handleQuantityChange = (id, value, index) => {
  setItems((prev) => {
    const newItems = [...prev];
    const item = newItems[index];
    console.log(item)
    if (!item) return prev;

    // If quantity is 1 and action is 'dec', show alert and remove the item
    if (item.quantity === 1 && value === "dec") {
      setTopAlert({
        message: `Quantity of "${item.name}" cannot be less than 1. Removing this item.`,
        onConfirm: () => {
          setTopAlert(null);
          setItems((current) => current.filter((_, i) => i !== index));
        },
      });
      return prev; // Do not immediately remove, wait for OK
    }
   
    // Otherwise, update the quantity
    newItems[index] = {
      ...item,
      quantity:
        typeof value === "number"
          ? Math.max(1, value) // Direct input: ensure min 1
          : value === "inc"
          ? item.quantity + 1 // Increment
          : Math.max(1, item.quantity - 1), // Decrement, min 1
    };

    return newItems;
  });

  setUnsavedChanges(true);
};


  // Add new item
  const handleAddItem = () => {
    const trimmed = searchQuery.trim();
    if (!trimmed) return showTopAlert("Enter a valid product name", "error");

    const found = allProducts.find(
      (p) => p.product_name.toLowerCase() === trimmed.toLowerCase()
    );

    const newItem = {
      name: found ? found.product_name : trimmed,
      product_id: found?._id,
      product_category_id: found?.product_category_id?._id,
      UOM_id: found?.UOM_id,
      unit: found?.UOM_id?.UOM || "Unit",
      image: found?.product_image ? `${imageurl}${found.product_image}` : null,
      quantity: 1,
      is_available: found ? true : false,
    };

    setItems((prev) => [...prev, newItem]);
    setSearchQuery("");
    setShowSuggestions(false);
    setUnsavedChanges(true);
    showTopAlert("Item added successfully!", "success");
  };

  // Save MR items
  const handleSaveItems = async () => {
    try {
          const encryptedToken = sessionStorage.getItem('token');
              const token = decryptData(encryptedToken);

      const MR_Data = {
        _id:MRitem._id,
        material_requirement_id: MRDetails._id,
        price_eligibility:
          pricingModel === "project" ? "Project Pricing" : "General Pricing",
        product_category_ids: [
          ...new Set(items.map((i) => i.product_category_id)),
        ],
        items: items.map((i) => ({
          ...i,
          product_name: i.name,
        })),
      };

      const res = await fetch(
        `${fetchurl}customer/material-requirement/update-material-requirement-items`,
        {
          method: "POST",
          headers: { "x-token": token, "Content-Type": "application/json" },
          body: JSON.stringify({
            MRId: MRDetails._id,
            MR_items: MR_Data,
          }),
        }
      );

      const data = await res.json();
      if (data.success) {
        showPopupWithTimer(data.message, "200or201", true, 3000);
        fetchMrItems(MRDetails._id);
        setUnsavedChanges(false);
        setOriginalItems(JSON.parse(JSON.stringify(items)));
        onClose();
        showTopAlert("MR Items updated successfully!", "success");
      } else {
        showTopAlert("Failed to save items. Try again.", "error");
      }
    } catch (err) {
      console.error("Error saving items:", err);
      showTopAlert("Server error while saving items", "error");
    }
  };

  // Handle popup close with unsaved changes check
  const handleClose = () => {
  if (unsavedChanges) {
    setTopAlert({
      message: "You have unsaved changes. Closing will discard them.",
      onConfirm: () => {
        setTopAlert(null);
        setItems([]);  // discard changes
        setUnsavedChanges(false);
        onClose();      // close popup
      },
    });
    return;
  }
  //setItems([]);
  //setMRitem({})
  setUnsavedChanges(false);
  onClose();
};


  const filteredDeliveryAddresses = deliveryAddresses.map(addr => ({
    value: addr._id,
    addressType: addr.address_type,
    label: addr.area_sector_locality || "Address not available",
    fullData: addr
  }));

  const handleAddressSelect = async (address) => {
  setAddress(address);
  setFormData(prev => ({ ...prev, areaLocalitySector: address }));
  
  try {
    const results = await geocodeByAddress(address);
    const latLng = await getLatLng(results[0]);
    setFormData(prev => ({
      ...prev,
      latitude: latLng.lat,
      longitude: latLng.lng
    }));
  } catch (error) {
    console.error('Error getting coordinates:', error);
  }
};

const handleSaveAddress = async () => {
  try {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);

    const encryptedHubId = sessionStorage.getItem('hubId');
    const hubId = decryptData(encryptedHubId);

    // Backend required fields mapping
    const payload = {
      hubId: selectedCustomer?.zones?.[0]?.hubs?.[0] || hubId,
      customer_id: selectedCustomer?.id || customerId,           
      address_type: activeTab,                   
      area_sector_locality: formData.areaLocalitySector,  
      latitude: formData.latitude || '',           
      longitude: formData.longitude || '',         
      
      // Project specific (if project tab)
      ...(activeTab === 'project' && {
        project_name: formData.projectName,   
        landmark: formData.nearbyLandmark || '',    
      }),
      
      ...(activeTab === 'other' && {
        landmark: formData.nearbyLandmark || ''
      }),
      
      is_default: true,
      is_active: true
    };

    console.log('🗺️ Sending payload:', payload);

    const response = await fetch(`${fetchurl}customer/delivery-address/createDeliveryAddress`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-token': token,
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    console.log('📥 Response:', data);

    if (data.success) {
      // Refresh addresses
      await fetchDeliveryAddresses(
        selectedCustomer?._id || selectedCustomer?.id || customerId
      );
      setSelectedDeliveryAddress(data.data);
      
      // Reset form
      setFormData({ 
        projectName: '', 
        areaLocalitySector: '', 
        nearbyLandmark: '', 
        addressType: 'project',
        latitude: '',
        longitude: ''
      });
      
      showPopupWithTimer("Address Saved Successfully!", '200or201', true, 3000);
      setShowAddAddressModal(false);

    } else {
      console.error('❌ Save failed:', data.message);
      alert(data.message || 'Failed to save address');
    }
  } catch (error) {
    console.error('💥 Error:', error);
    alert('Network error. Please try again.');
  }
};

const fetchDeliveryAddresses = async (customerId) => {
  try {
    const encryptedToken = sessionStorage.getItem("token");
    const token = decryptData(encryptedToken);

    if (!customerId || !token) return;

    const response = await fetch(
      `${fetchurl}customer/delivery-address/getDeliveryAddresses?customer_id=${customerId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-token": token,
        },
      }
    );

    const data = await response.json();

    console.log("Delivery addresses for selected customer:", data);

  if (data.success) {
    const addresses = data.data || [];

    setDeliveryAddresses(addresses);

    const defaultAddress = addresses.find(
      address => address.is_default === true
    );

    if (defaultAddress) {
      setSelectedDeliveryAddress(defaultAddress);
    }
  }

  } catch (error) {
    console.error("Error fetching delivery addresses:", error);
    setDeliveryAddresses([]);
  }
};


const calculateTotals = (cartItems/* , redeemableItems */) => {

  let subtotal = 0;
  let taxAmount = 0;

  cartItems.forEach(cartItem => {

    // find the redeemable item by ID
    //const redeemItem = redeemableItems?.find(r => r.item_id === cartItem._id);

    // If redeem data not found → no coins
    //const coins = redeemItem ? redeemItem.coins : 0;

    // reduce selling price by coins
    //const effectivePrice = Math.max(cartItem.selling_price - coins, 0);

    // GST % (ex: 18)
    const gstRate = cartItem.GST || cartItem.IGST || 0;
    const eachSubtotal = cartItem.selling_price * cartItem.quantity;
    const effectivePrice = eachSubtotal /* - coins */;
    // Subtotal calculation (normal selling price × qty)
    subtotal += cartItem.selling_price * cartItem.quantity;

    // Correct tax calculation
    taxAmount += (effectivePrice * (gstRate / 100));
  });

  // Round the tax
  taxAmount = Math.round(taxAmount);

  return { subtotal, taxAmount };
};

 const createAndSendQuotation = async () => {
  setIsSubmitting(true);
  try {
      console.log('🆕 Creating new quotation');
      // Get cart items from state (only for new quotations)
      if (!items.length) {
        return { success: false, message: 'Add items to create RFQ' };
      }

    console.log("items for rfq:",items);  

    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    const enhubId = sessionStorage.getItem('hubId')
    const hubId = decryptData(enhubId);

    const customerid = selectedCustomer?.id || customerId;

    if (!customerid || !token || !hubId) {
      return { success: false, message: 'User information is incomplete or missing' };
    }

    let quotationPayload;
    const { subtotal, taxAmount } = calculateTotals(items/*,  redeemableItems */);
            
    // Calculate total amount including tax and discount
    const totalAmount = subtotal + taxAmount;
    // Extract unique product category IDs from cart items
    const product_category_ids = [
      ...new Set(items.map(item => item.product_category_id))
    ];
    // Build quotation payload
    quotationPayload = {
      quotation_date: new Date().toISOString(),
      customer_id:customerid ,
      project_id: selectedProject?.id || null,
      delivery_address_id: selectedDeliveryAddress?._id || null,
      tax_preference: "Taxable",
      hubId: hubId,
      status: "Pending",
      is_digistore: true,
      material_requirement_id: MRDetails._id,
      MR_items_id: MRitem._id,

      // Map cart items into quotation items
      items: items.map(item => {
        const totalPrice = item.selling_price * item.quantity;
        const taxRate = item.tax_rate / 100;
        const totalTaxAmount = totalPrice * taxRate;
        const totalItemAmount = totalPrice + totalTaxAmount;

        console.log('Calculating totals for item:', item);

        return {
          item_id: item.product_id,
          item_image: item.product_image || item.image,
          item_name: item.name,
          quantity: item.quantity,
          selling_price: item.selling_price,
          cost_price: item.cost_price,
          tax_rate: Math.round(taxRate),
          total_tax_amount: Math.round(+totalTaxAmount),
          total: Math.round(+totalItemAmount),
          UOM_id: item?.UOM_id,
        };
      }),


      product_category_ids: product_category_ids,
      subtotal: subtotal,
      tax_amount: taxAmount,
      discount: 0,
      total: totalAmount ,
      terms_and_conditions: "Standard terms and conditions apply",
    };


    console.log('📤 Sending quotation data:', quotationPayload);

    const endpoint = `${fetchurl}customer/quotations/save-quickbuy-quotation`;
    const method = 'POST';

    // Send to backend
    const response = await fetch(endpoint, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'x-token': token,
      },
      body: JSON.stringify(quotationPayload)
    });

    const data = await response.json();
    console.log('📊 Backend response:', data);
   if (response.status === 200 || response.status === 201) {
      // ✅ STEP 1: Show success popup
      setPopupMessage("RFQ Created Successfully!");
      setPopupType('200or201');
      setShowPopup(true);

      // ✅ STEP 2: Reload MR items and keep loader visible while refreshing
      if (fetchMrItems && typeof fetchMrItems === 'function') {
        await fetchMrItems(MRDetails._id);
      }

      // ✅ STEP 3: Auto-close everything after a short delay
      setTimeout(() => {
        console.log('🧹 Auto-closing modal and clearing state...');
        setShowPopup(false);        // Close popup first
        onClose();                  // Close modal
        setItems([]);               // Clear items
      }, 1200);
      return { success: true, quotationNumber: data.data.quotation_number }

    } else {
      return { success: false, message: data.message || 'Failed to process quotation' };
    }

  } catch (error) {
    console.error('Error processing quotation:', error);
    return { success: false, message: 'An error occurred while processing the quotation' };
  } finally {
    setIsSubmitting(false);
  }
}; 

  // Filter search suggestions
  const filteredProducts =
    allProducts.filter((item) =>
      item.product_name?.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

  const imageRejectionReasons = [
    { value: "invalid_image", label: "Invalid image" },
    { value: "blurry_image", label: "Blurry image" },
    { value: "category_items_not_available", label: "Category items not available" },
    { value: "some_items_not_available", label: "Some items not available" },
  ];

  const getReasonMessage = (reasonValue) => {
    const selectedReason = imageRejectionReasons.find((reason) => reason.value === reasonValue);
    const reasonText = selectedReason ? selectedReason.label : "selected reason";
    return `Hello, the uploaded image was rejected due to ${reasonText}. Please upload the image again.`;
  };

  const handleSendImageRejectionMessage = () => {
    if (!selectedImageReason) return;

    const customerPhone = MRDetails?.customer?.mobile_number || MRDetails?.customer?.phone || "";
    const phone = String(customerPhone).replace(/\D/g, "");

    if (!phone) {
      alert("Customer phone number is not available.");
      return;
    }

    const message = getReasonMessage(selectedImageReason);
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const handleRejectAndNotify = async () => {
    if (!selectedImageReason) return;

    try {
      const data = await rejectMaterialRequirementsImage(
        MRDetails.customer._id,
        MRDetails._id,
        selectedImageReason
      );

      showPopupWithTimer(data.message || "Rejection sent to customer successfully", "200or201", true, 3000);

      if (fetchMrItems && typeof fetchMrItems === "function") {
        await fetchMrItems(MRDetails._id);
      }

      setTimeout(() => {
        setShowPopup(false);
        onClose();
      }, 1200);
    } catch (error) {
      showPopupWithTimer(error.message || "Failed to send rejection message", "400", false);
    }
  };

  if (!isOpen) return null;


  const rejectMaterialRequirementsImage = async (customerId, MR_ID, reason) => {
  try {
    const response = await fetch(
      `${fetchurl}customer/material-requirement/reject-material-requirements-image`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          customerId,
          MR_ID,
          reason,
        }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to reject material requirement image");
    }

    return data;
  } catch (error) {
    console.error("Error rejecting material requirement image:", error);
    throw error;
  }
};


  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/40 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl w-full max-w-7xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
       
       {topAlert && (
  <TopAlert 
    message={topAlert.message} 
    onConfirm={topAlert.onConfirm} 
  />
)}
       {isSubmitting && (
         <div className="absolute inset-0 z-50 bg-white/80 backdrop-blur-sm flex items-center justify-center p-4">
           <div className="flex flex-col items-center gap-3 rounded-2xl bg-white/95 border border-gray-200 px-6 py-5 shadow-lg">
             <div className="h-10 w-10 rounded-full border-4 border-blue-300 border-t-blue-600 animate-spin"></div>
             <p className="text-sm font-semibold text-gray-700">Submitting and refreshing...</p>
           </div>
         </div>
       )}


        {/* Header */}
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">
            Material Requirement Items
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition"
          >
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Main Content */}
        <div className="flex overflow-hidden">
          {/* Left Section */}
          <div className="w-1/2 flex flex-col items-center justify-start p-3 bg-gray-50">
            <ImageGallery attachments={MRDetails?.material_list_attachments || []} fetchurl={fetchurl} />
          </div>

          {/* Right Section */}
          <div className="w-1/2 flex flex-col bg-white px-8 py-5 overflow-hidden">
          <div className="flex flex-row gap-2 mb-2">
            <p className="text-sm font-semibold text-gray-800 mb-2">
              Status: 
            </p>
            <p className={`px-2 py-1 rounded-full text-sm font-semibold ${
            getStatusBadgeClass(MRDetails.status)
            }`}>
              {MRDetails.status}
            </p>
          </div>
            {/* Pricing Type */}
            <div className="mb-6">
              <p className="text-sm font-semibold text-gray-800 mb-2">
                Select Pricing Type
              </p>
              <div className="flex gap-3">
                {["project", "general"].map((type) => (
                  <div
                    key={type}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all cursor-pointer ${
                      pricingModel === type
                        ? "border-blue-600 bg-blue-50 shadow-sm"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    onClick={() => {
                      setPricingModel(type);
                      setUnsavedChanges(true);
                    }}
                  >
                    <div
                      className={`w-3 h-3 rounded-full ${
                        pricingModel === type ? "bg-blue-600" : "bg-gray-300"
                      }`}
                    ></div>
                    <span
                      className={`text-sm font-medium ${
                        pricingModel === type
                          ? "text-blue-700"
                          : "text-gray-700"
                      }`}
                    >
                      {type === "project"
                        ? "Project Pricing"
                        : "General Pricing"}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Search + Add */}
            <div className="flex items-start gap-3 mb-4">
              <div className="relative flex-1">
                <Search
                  className="absolute left-3 top-3 text-gray-400"
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Search or add an item..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSuggestions(true);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl outline-none focus:border-blue-400 text-sm transition"
                />
                {showSuggestions && searchQuery && (
                  <div className="absolute bg-white border border-gray-200 rounded-xl mt-1 w-full shadow-lg z-10 max-h-48 overflow-y-auto">
                    {filteredProducts.length > 0 ? (
                      filteredProducts.map((item) => (
                        <div
                          key={item._id}
                          onClick={() => {
                            setSearchQuery(item.product_name);
                            setShowSuggestions(false);
                          }}
                          className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-700"
                        >
                          {item.product_name}
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-sm text-gray-500">
                        No items found
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button
  onClick={handleAddItem}
  className="text-sm font-medium py-2 px-4 rounded-md border border-[#f7525a] text-[#f7525a] bg-white hover:bg-[#f7525a] hover:text-white transition-colors duration-200 shadow-sm"
>
  Add
</button>

            </div>

      <div className="w-full flex flex-row items-center mb-4 mt-5 space-x-2">
        <label htmlFor="deliveryAddress" className="block text-sm font-regular text-[#e54643] w-1/6">
          Delivery Address<span className="text-red-500">*</span>
        </label>
        <div className="w-1/3 space-y-2">
        <CustomDropdown
          options={filteredDeliveryAddresses}
          selected={
            selectedDeliveryAddress
              ? {
                  value: selectedDeliveryAddress._id,
                  label:
                    selectedDeliveryAddress.area_sector_locality ||
                    "Address not available",
                  addressType: selectedDeliveryAddress.address_type
                }
              : null
          }
          onSelect={(option) => {
            if (option.value === "add-new") {
              setShowAddAddressModal(true);
            } else {
              setSelectedDeliveryAddress(option.fullData);
            }
          }}
          placeholder="Select Delivery Address"
          showSearch={true}
          showAddNew={true}
        />
        </div>
      </div>


            {/* Items List */}
            <div className="flex-1 overflow-y-auto mb-4 space-y-3">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between border border-gray-100 rounded-xl p-3 hover:shadow-sm bg-white transition"
                >
                  <div className="flex items-center gap-3">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-10 h-10 rounded-md object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-md bg-gray-200 flex items-center justify-center">
                        <FontAwesomeIcon
                          icon={faImage}
                          className="text-gray-400"
                        />
                      </div>
                    )}
                    <span className="font-medium text-gray-800 text-sm">
                      {item.name}
                    </span>
                  </div>
                  {/* Right: QuantitySelector + Delete Button */}
             <div className="flex items-center gap-2">
                  <QuantitySelector
                    item={item}
                    quantity={item.quantity}
                    handleQuantityChange={handleQuantityChange}
                    index={index}
                  />
                   <button
                  onClick={() => {
                    setItems((prev) => prev.filter((_, i) => i !== index));
                    setUnsavedChanges(true); // Mark as unsaved changes
                  }}
                  className="p-2 hover:bg-red-50 rounded-full transition"
                  title="Remove item"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
                </div>

                </div>
              ))}
            </div>

            { (MRDetails.status === "Received" || MRDetails.status === "Verified") && (  
            <div className="w-full mt-4 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Rejection Reason
              </label>
              <div className="flex items-center gap-2">
                <select
                  value={selectedImageReason}
                  onChange={(e) => setSelectedImageReason(e.target.value)}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                >
                  <option value="">Select a reason</option>
                  {imageRejectionReasons.map((reason) => (
                    <option key={reason.value} value={reason.value}>
                      {reason.label}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={handleRejectAndNotify}
                  disabled={!selectedImageReason}
                  className={`p-2.5 rounded-lg transition ${selectedImageReason ? "bg-green-600 text-white hover:bg-green-700" : "bg-gray-200 text-gray-500 cursor-not-allowed"}`}
                  title="Send WhatsApp message"
                >
                  <FontAwesomeIcon icon={faPaperPlane} className="h-4 w-4" />
                </button>
              </div>
            </div>
            )}

            {/* Footer */}
            <div className="flex justify-end gap-3 border-t pt-4 mt-auto">
              <button
                onClick={handleClose}
                className="px-5 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
              >
                Cancel
              </button> 
              {unsavedChanges && MRDetails.status === "Received" && (
                <button
                  onClick={handleSaveItems}
                  className="px-5 py-2 text-sm font-medium border border-[#f7525a] text-[#f7525a] bg-white hover:bg-[#f7525a] hover:text-white transition-colors duration-200 rounded-lg shadow-sm transition"
                >
                  Save
                </button>
              )}
               {MRDetails.status === "Verified" && (
              <button
                onClick={createAndSendQuotation}
                disabled={isSubmitting}
                className={`px-5 py-2 text-sm font-medium border rounded-lg shadow-sm transition-colors duration-200 ${isSubmitting ? 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed' : 'border border-[#f7525a] text-[#f7525a] bg-white hover:bg-[#f7525a] hover:text-white'}`}
              >
                {isSubmitting ? 'Creating RFQ...' : 'Create RFQ'}
              </button>
               )} 
            </div>
          </div>
        </div>
      </div>
      {showAddAddressModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">
            
            {/* Header - YOUR ORIGINAL */}
            <div className="flex justify-between items-center p-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <h3 className="text-md font-semibold ">Add New Address</h3>
              </div>
              <button onClick={() => setShowAddAddressModal(false)}>
                <FontAwesomeIcon icon={faX} className="w-3 h-3" />
              </button>
            </div>
      
            <div className="flex-1 overflow-y-auto p-4">
              
              {/* YOUR TABS - SAME */}
              <div className="flex bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-1 mb-4 shadow-md">
                {['project', 'other'].map((tab) => (
                  <button
                    key={tab}
                    className={`flex-1 py-2 px-4 text-xs font-semibold rounded-lg transition-all ${
                      activeTab === tab
                        ? 'bg-blue-500 shadow-sm text-white border border-blue-200'
                        : 'text-gray-600 hover:bg-white'
                    }`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
      
              {/* Form Fields - YOUR ORIGINAL */}
              <div className="space-y-4">
                {activeTab === 'project' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Project Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      value={formData.projectName || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, projectName: e.target.value }))}
                      className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 text-sm"
                      placeholder="Enter project name"
                    />
                  </div>
                )}
      
                {/* WORKING MAP SECTION - NEW */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">
          Area / Locality / Sector <span className="text-red-500">*</span>
        </label>
        
        <PlacesAutocomplete 
          value={address} 
          onChange={setAddress} 
          onSelect={handleAddressSelect}
          searchOptions={{ 
            types: ['geocode'], 
            componentRestrictions: { country: 'in' } 
          }}
        >
          {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
            <div className="relative">
              {/* FIXED: value prop use chey, getInputProps lo value ivvaku */}
              <input
                {...getInputProps({
                  placeholder: 'Search area, apartment or pincode...',
                  className: 'w-full p-3 pr-10 border border-gray-200 rounded-lg focus:ring-2 focus:border-blue-500 text-sm'
                })}
              />
              
              <FontAwesomeIcon icon={faSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              
              {/* Loading */}
              {loading && (
                <div className="absolute mt-10 bg-white border border-gray-200 rounded-lg w-full shadow-lg p-3 z-20">
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                    Searching...
                  </div>
                </div>
              )}
              
              {/* Suggestions */}
              {suggestions.length > 0 && (
                <div className="absolute mt-1 bg-white border border-gray-200 rounded-lg w-full shadow-lg z-[9999] max-h-48 overflow-y-auto">
                  {suggestions.map(suggestion => (
                    <div 
                      key={suggestion.placeId}
                      {...getSuggestionItemProps(suggestion, {
                        className: suggestion.active 
                          ? 'px-4 py-2.5 bg-blue-500 text-white cursor-pointer flex items-center' 
                          : 'px-4 py-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0 flex items-center'
                      })}
                    >
                      <FontAwesomeIcon icon={faMapMarkerAlt} className={`w-4 h-4 mr-3 ${suggestion.active ? 'text-white' : 'text-gray-400'}`} />
                      <span className="text-sm">{suggestion.description}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </PlacesAutocomplete>
      </div>
      
                {/* Landmark - YOUR ORIGINAL */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-4">
                    Nearby Landmark
                  </label>
                  <input
                    value={formData.nearbyLandmark || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, nearbyLandmark: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 text-sm"
                    placeholder=" "
                  />
                </div>
              </div>
            </div>
      
            {/* Footer - YOUR ORIGINAL */}
            <div className="p-4 border-t bg-gradient-to-r from-gray-50 to-white">
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowAddAddressModal(false)}
                  className="px-6 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAddress}
                  disabled={!formData.areaLocalitySector}
                  className="px-6 py-2 text-xs font-bold text-white bg-blue-600 rounded-lg shadow-md"
                >
                  Save Address
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const QuantitySelector = ({ item, quantity, handleQuantityChange, min = 1, max = Infinity, index }) => {
  const [inputValue, setInputValue] = useState(quantity.toString());

  // Sync inputValue with quantity when quantity changes (e.g., from buttons)
  useEffect(() => {
    setInputValue(quantity.toString());
  }, [quantity]);

  // Handle input change (allow any typing, including clearing)
  const handleInputChange = (e) => {
    setInputValue(e.target.value); // Update local state freely
  };

  // Handle blur (validate and update parent)
  const handleInputBlur = () => {
    const numValue = parseInt(inputValue, 10);
    let finalValue;

    if (isNaN(numValue) || numValue < min) {
      finalValue = min; // Clamp to min if invalid or too low
    } else if (numValue > max) {
      finalValue = max; // Clamp to max if too high
    } else {
      finalValue = numValue; // Valid number
    }

    handleQuantityChange(item.id, finalValue, index); // Update parent
    setInputValue(finalValue.toString()); // Sync local state
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => handleQuantityChange(item.id, "dec", index)}
        className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded-full hover:bg-gray-100"
      >
        <Minus size={14} />
      </button>
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        className="w-12 text-center text-sm border border-gray-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
        min={min}
        max={max}
      />
      <button
        onClick={() => handleQuantityChange(item.id, "inc", index)}
        className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded-full hover:bg-gray-100"
        disabled={quantity >= max}
      >
        <Plus size={14} />
      </button>
    </div>
  );
};


// Top Alert Component
const TopAlert = ({ message, onConfirm }) => {
  return (
    <div
      className="absolute top-4 left-1/2 transform -translate-x-1/2 px-4 py-3 rounded-lg border bg-gray-100 text-gray-800 border-gray-300 shadow-md flex items-center justify-between gap-4 w-[90%] max-w-lg z-50"
    >
      <span>{message}</span>
      <button
        onClick={onConfirm}
        className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-md text-sm font-medium transition"
      >
        OK
      </button>
    </div>
  );
};


export default ViewMRitems;
